import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy, Phone, MessageSquare, Flame, TrendingUp, Loader2, Crown,
} from "lucide-react";

const RANGES = [
  { key: 7,  label: "This week" },
  { key: 30, label: "This month" },
  { key: 90, label: "Last 90 days" },
];

function Medal({ rank }) {
  if (rank === 1) return <Crown size={14} className="text-yellow-500" />;
  if (rank === 2) return <span className="text-neutral-400 font-bold text-sm">2</span>;
  if (rank === 3) return <span className="text-amber-700 font-bold text-sm">3</span>;
  return <span className="text-muted-foreground text-xs">{rank}</span>;
}

function Bar({ value, max, className }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 4;
  return (
    <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden">
      <div className={`h-full ${className}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function TeamLeaderboard({ hideHeader = false }) {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [since, setSince] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/leaderboard", { params: { days } });
      setRows(data.rows || []);
      setSince(data.since || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leaderboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxCalls = Math.max(1, ...rows.map((r) => r.calls));
  const maxSms   = Math.max(1, ...rows.map((r) => r.sms));
  const maxScore = Math.max(1, ...rows.map((r) => r.score));

  const totals = rows.reduce(
    (a, r) => ({
      calls: a.calls + r.calls,
      sms:   a.sms   + r.sms,
      deals: a.deals + r.deals_closed,
    }),
    { calls: 0, sms: 0, deals: 0 },
  );

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="leaderboard-page">
      <div className="flex items-center justify-between reveal">
        {!hideHeader ? (
          <div>
            <span className="app-eyebrow">/ Team</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3 flex items-center gap-2">
              <Trophy className="text-yellow-500" size={32} />
              Leaderboard
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
              Live scoreboard of who's calling, texting and closing the most deals.
              Refreshes automatically as your team works.
            </p>
          </div>
        ) : <div />}
        <div className="flex gap-1 rounded-full border border-[var(--app-line)] p-1 bg-white"
             data-testid="range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setDays(r.key)}
              data-testid={`range-${r.key}`}
              className={`px-3 py-1 text-xs uppercase tracking-widest rounded-full transition-colors ${
                days === r.key
                  ? "bg-[var(--app-black)] text-white"
                  : "text-muted-foreground hover:bg-[var(--app-surface)]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-8">
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Calls placed</div>
          <div className="kpi-num text-3xl mt-1" data-testid="tot-calls">{totals.calls}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">SMS sent</div>
          <div className="kpi-num text-3xl mt-1" data-testid="tot-sms">{totals.sms}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Deals closed</div>
          <div className="kpi-num text-3xl mt-1" data-testid="tot-deals">{totals.deals}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Teammates active</div>
          <div className="kpi-num text-3xl mt-1" data-testid="tot-active">
            {rows.filter((r) => r.score > 0).length}
          </div>
        </div>
      </div>

      {/* Leaderboard table */}
      <div className="card-flat p-0 overflow-hidden mt-6">
        <div className="px-4 py-3 border-b border-[var(--app-line)] bg-[var(--app-surface)] flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {rows.length} teammate{rows.length === 1 ? "" : "s"} · since {since ? new Date(since).toLocaleDateString() : "—"}
          </div>
          {loading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
        </div>
        {rows.length === 0 && !loading ? (
          <div className="p-8 text-sm text-muted-foreground text-center">
            No teammates yet. Invite your team from the Team page.
          </div>
        ) : (
          <div className="divide-y divide-[var(--app-line)]" data-testid="leaderboard-rows">
            {rows.map((r) => (
              <div key={r.user_id}
                   className={`px-4 py-3 grid grid-cols-12 gap-3 items-center ${
                     r.rank === 1 ? "bg-yellow-50/40" : ""
                   }`}
                   data-testid={`leader-${r.user_id}`}>
                <div className="col-span-1 flex items-center justify-center">
                  <Medal rank={r.rank} />
                </div>
                <div className="col-span-4 min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {r.role} · {r.email}
                  </div>
                </div>
                <div className="col-span-2 text-xs">
                  <div className="flex items-center gap-1 text-blue-700">
                    <Phone size={11} /> {r.calls}
                  </div>
                  <Bar value={r.calls} max={maxCalls} className="bg-blue-500 mt-1" />
                </div>
                <div className="col-span-2 text-xs">
                  <div className="flex items-center gap-1 text-emerald-700">
                    <MessageSquare size={11} /> {r.sms}
                  </div>
                  <Bar value={r.sms} max={maxSms} className="bg-emerald-500 mt-1" />
                </div>
                <div className="col-span-1 text-xs">
                  <div className="flex items-center gap-1 text-orange-600">
                    <Flame size={11} /> {r.hot_leads}
                  </div>
                </div>
                <div className="col-span-1 text-xs">
                  <div className="flex items-center gap-1 text-violet-700 font-semibold">
                    <TrendingUp size={11} /> {r.deals_closed}
                  </div>
                </div>
                <div className="col-span-1 text-right">
                  <div className="text-xl font-display font-bold">
                    {r.score}
                  </div>
                  <Bar value={r.score} max={maxScore}
                       className="bg-[var(--app-black)] mt-1" />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="px-4 py-2 border-t border-[var(--app-line)] bg-[var(--app-surface)] text-[10px] text-muted-foreground grid grid-cols-12 gap-3">
          <div className="col-span-1">#</div>
          <div className="col-span-4">Teammate</div>
          <div className="col-span-2">Calls</div>
          <div className="col-span-2">Texts</div>
          <div className="col-span-1">Hot/Warm</div>
          <div className="col-span-1">Closed</div>
          <div className="col-span-1 text-right">Score</div>
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground mt-3">
        Score = calls (×1) + SMS (×1) + hot/warm leads owned (×5) + deals closed (×20). Weights are tuned to reward closing over dialing.
      </div>
    </div>
  );
}
